import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BarChart3, TrendingUp, DollarSign, ShoppingCart, Calendar, FileDown, FileSpreadsheet, Users, Ticket, XCircle, Calculator, PieChart, Layers, Package } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useReports } from "@/hooks/useReports";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { SalesChart } from "@/components/reports/SalesChart";
import { PaymentMethodsChart } from "@/components/reports/PaymentMethodsChart";
import { TopProductsTable } from "@/components/reports/TopProductsTable";
import { CategorySalesChart } from "@/components/reports/CategorySalesChart";
import { exportToPDF, exportToExcel } from "@/lib/report-export";

const reportNavItems = [
  { title: "Vendas", url: "/dashboard/relatorios", icon: BarChart3, description: "Análise de vendas" },
  { title: "DRE", url: "/dashboard/dre", icon: FileSpreadsheet, description: "Demonstrativo de resultado" },
  { title: "Combos", url: "/dashboard/relatorio-combos", icon: Layers, description: "Vendas de combos" },
  { title: "Ticket Médio", url: "/dashboard/ticket-medio", icon: Users, description: "Média por pessoa" },
  { title: "Entradas", url: "/dashboard/relatorio-entradas", icon: Ticket, description: "Couvert e entradas" },
  { title: "Cancelamentos", url: "/dashboard/cancelamentos", icon: XCircle, description: "Itens cancelados" },
  { title: "CMV", url: "/dashboard/cmv", icon: Calculator, description: "Custo de mercadoria" },
  { title: "Margens", url: "/dashboard/margens", icon: PieChart, description: "Lucro por produto" },
  { title: "Por Grupo", url: "/dashboard/relatorio-grupo", icon: Package, description: "Vendas por categoria" },
  { title: "Emissões Fiscais", url: "/dashboard/relatorio-fiscal-emissoes", icon: TrendingUp, description: "NFC-e emitidas" },
];

type DatePreset = "today" | "7days" | "30days" | "thisMonth" | "custom";

export default function RelatoriosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [datePreset, setDatePreset] = useState<DatePreset>("30days");
  const [dateFrom, setDateFrom] = useState<Date>(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState<Date>(new Date());
  const { activeRestaurant } = useRestaurantContext();

  const { data, isLoading } = useReports({ dateFrom, dateTo });

  const handleExportPDF = () => {
    if (!data) return;
    exportToPDF({ data, dateFrom, dateTo, restaurantName: activeRestaurant?.name });
  };

  const handleExportExcel = () => {
    if (!data) return;
    exportToExcel({ data, dateFrom, dateTo, restaurantName: activeRestaurant?.name });
  };

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    const now = new Date();

    switch (preset) {
      case "today":
        setDateFrom(now);
        setDateTo(now);
        break;
      case "7days":
        setDateFrom(subDays(now, 7));
        setDateTo(now);
        break;
      case "30days":
        setDateFrom(subDays(now, 30));
        setDateTo(now);
        break;
      case "thisMonth":
        setDateFrom(startOfMonth(now));
        setDateTo(endOfMonth(now));
        break;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-warning" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Relatórios</h1>
            <p className="text-sm text-muted-foreground">
              Análise de vendas e desempenho
            </p>
        </div>

        {/* Report Navigation Hub */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {reportNavItems.map((item) => {
            const isCurrentPage = location.pathname === item.url;
            return (
              <Card 
                key={item.url}
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md",
                  isCurrentPage 
                    ? "border-primary bg-primary/5 ring-1 ring-primary/20" 
                    : "hover:border-primary/50"
                )}
                onClick={() => !isCurrentPage && navigate(item.url)}
              >
                <CardContent className="p-4 flex flex-col items-center gap-2 text-center">
                  <item.icon className={cn(
                    "h-6 w-6",
                    isCurrentPage ? "text-primary" : "text-muted-foreground"
                  )} />
                  <span className={cn(
                    "text-sm font-medium",
                    isCurrentPage && "text-primary"
                  )}>{item.title}</span>
                  {!isCurrentPage && (
                    <span className="text-xs text-muted-foreground hidden sm:block">
                      {item.description}
                    </span>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

        <div className="flex flex-wrap items-center gap-2">
          {(["today", "7days", "30days", "thisMonth"] as DatePreset[]).map((preset) => (
            <Button
              key={preset}
              variant={datePreset === preset ? "default" : "outline"}
              size="sm"
              onClick={() => handlePresetChange(preset)}
            >
              {preset === "today" && "Hoje"}
              {preset === "7days" && "7 dias"}
              {preset === "30days" && "30 dias"}
              {preset === "thisMonth" && "Este mês"}
            </Button>
          ))}

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={datePreset === "custom" ? "default" : "outline"}
                size="sm"
                className="gap-2"
              >
                <Calendar className="h-4 w-4" />
                Personalizado
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="flex">
                <div className="border-r p-3">
                  <p className="text-sm font-medium mb-2">De</p>
                  <CalendarComponent
                    mode="single"
                    selected={dateFrom}
                    onSelect={(date) => {
                      if (date) {
                        setDateFrom(date);
                        setDatePreset("custom");
                      }
                    }}
                    locale={ptBR}
                  />
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium mb-2">Até</p>
                  <CalendarComponent
                    mode="single"
                    selected={dateTo}
                    onSelect={(date) => {
                      if (date) {
                        setDateTo(date);
                        setDatePreset("custom");
                      }
                    }}
                    locale={ptBR}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={!data || isLoading}
            className="gap-2"
          >
            <FileDown className="h-4 w-4" />
            PDF
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            disabled={!data || isLoading}
            className="gap-2"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total de Vendas
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <p className="text-2xl font-bold">{formatCurrency(data?.totalSales || 0)}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total de Pedidos
            </CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <p className="text-2xl font-bold">{data?.totalOrders || 0}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ticket Médio
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <p className="text-2xl font-bold">{formatCurrency(data?.averageTicket || 0)}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SalesChart data={data?.dailySales || []} isLoading={isLoading} />
        <PaymentMethodsChart data={data?.paymentMethodSales || []} isLoading={isLoading} />
        <TopProductsTable data={data?.topProducts || []} isLoading={isLoading} />
        <CategorySalesChart data={data?.categorySales || []} isLoading={isLoading} />
      </div>
    </div>
  );
}
